import { CommonModule, Location } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent } from '@app/components/button';
import { CardComponent } from '@app/components/card';
import { HeadingComponent } from '@app/components/heading';
import {
  HorizontalAlignment,
  ImgCardComponent,
} from '@app/components/img-card';
import { SpinnerComponent } from '@app/components/spinner';
import { TabsComponent, TabsItemComponent } from '@app/components/tabs';
import { TitleComponent } from '@app/components/title-section';
import { AdminChallengesService } from '@app/services/admin-challenges/admin-challenges.service';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { AdminThemeService } from '@app/services/admin-theme/admin-theme.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { TranslatePipe } from '@ngx-translate/core';
import {
  GroupChallenge,
  ProgramWorkoutSchedule,
  TenantTheme,
} from '@skyfit/core-data-library';
import { switchMap } from 'rxjs';
import { GroupChallengeRoute } from '../challenges-route';

@Component({
  selector: 'app-challenge-details',
  templateUrl: './challenge-details.component.html',
  styleUrl: './challenge-details.component.css',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    CardComponent,
    HeadingComponent,
    TranslatePipe,
    ButtonComponent,
    ImgCardComponent,
    TabsComponent,
    TabsItemComponent,
    TitleComponent,
    HeadingComponent,
  ],
})
export class ChallengeDetailsComponent implements OnInit {
  private readonly challengesService = inject(AdminChallengesService);
  private readonly translationService = inject(TranslatorService);
  private readonly tenantService = inject(AdminTenantService);
  private readonly adminThemeService = inject(AdminThemeService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  theme?: TenantTheme;
  isLoading = signal(true);
  challenge = signal<GroupChallenge>(undefined);
  schedule = signal<ProgramWorkoutSchedule>(undefined);
  HorizontalAlignment = HorizontalAlignment;
  subtitle = computed(() => {
    return this.challengesService.formatDuration(
      this.challenge()?.durationInDays
    );
  });
  categories = computed(() => {
    const stages = this.schedule()?.stages;
    if (stages) {
      const categories = new Array<string>();
      for (const stage of stages) {
        for (const workout of stage.workouts) {
          categories.push(workout.cls.displayTypeName);
        }
      }
      categories.sort();
      return [...new Set(categories)].join(', ');
    }
    return '';
  });
  id: number;

  ngOnInit(): void {
    this.route.params
      .pipe(
        switchMap((params) => {
          this.id = this.challengesService.decodeChallengeId(params.id);
          return this.challengesService.getChallenge(this.id);
        })
      )
      .pipe(
        switchMap((challenge) => {
          const tenantId = this.tenantService.getTenantId();
          if (
            !this.challengesService.canTenantAccessChallenge(
              challenge,
              tenantId
            )
          ) {
            this.router.navigate(['challenges']);
            return;
          }
          this.challenge.set(challenge);
          return this.challengesService.getChallengeScheduleByCategoryId(
            challenge.dynamicCategoryObjectId
          );
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((schedule) => {
        this.schedule.set(schedule);
        this.isLoading.set(false);
      });

    this.theme = this.adminThemeService.getCurrentTheme();
  }

  backgroundUrl(url: string): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(`url(${url})`);
  }

  async createChallenge(): Promise<void> {
    await this.router.navigate(
      [
        'challenges',
        'create',
        this.challengesService.encodeChallengeId(this.id),
      ],
      { queryParams: { from: GroupChallengeRoute.challengeDetails } }
    );
  }

  goBack(): void {
    this.router.navigate(['challenges', 'select']);
  }
}
